/**
 * Templates — SKNOTIFICATION-GLOBAL-CR-001. The screen UICC-001 §1 says is
 * missing entirely ("no template authoring UI at all"): list a tenant's
 * templates, create a new one (name + message-class), then drill into a
 * template to author its variants and manage its two gates.
 */
import React, { useEffect, useState } from 'react';
import { View, Text, ScrollView, TextInput, Pressable, StyleSheet } from 'react-native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../../context/themeContext';
import { useResponsive } from '../../lib/responsive';
import { useToast } from '../../components/toast';
import Icon from '../../components/notif/Icon';
import { PageHeader, EmptyState, Btn, Banner, SectionLabel } from '../../components/notif/ui';
import { Select } from '../../components/notif/select';
import { listTemplates, createTemplate } from '../../services/notification/governance';
import { handleApiError } from '../../utils/errorHandler';
import { relTime } from '../../utils/formatters';
import { MESSAGE_CLASSES } from '../../schema/governance';
import type { BackendTemplate } from '../../schema/governance';
import type { AppStackParamList } from '../../navigation/types';

const CLASS_LABEL: Record<string, string> = {
  authentication: 'Authentication',
  transactional: 'Transactional',
  service: 'Service',
  promotional: 'Promotional',
};

export default function TemplatesScreen() {
  const { theme: { colors } } = useTheme();
  const { pad } = useResponsive();
  const toast = useToast();
  const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();

  const [templates, setTemplates] = useState<BackendTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  const [templateKey, setTemplateKey] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [messageClassCode, setMessageClassCode] = useState<(typeof MESSAGE_CLASSES)[number]>('promotional');
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    setErr(false);
    listTemplates()
      .then(setTemplates)
      .catch((e) => { setErr(true); handleApiError(e, "Couldn't load templates"); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const canCreate = !!templateKey.trim() && !!displayName.trim() && !creating;

  const create = async () => {
    if (!canCreate) return;
    setCreating(true);
    try {
      const template = await createTemplate({
        templateKey: templateKey.trim(),
        displayName: displayName.trim(),
        messageClassCode,
      });
      toast.success('Template created', { message: template.displayName });
      setTemplateKey('');
      setDisplayName('');
      load();
      navigation.navigate('templateDetail', { templateId: template.templateId });
    } catch (e) {
      handleApiError(e, 'Could not create template');
    } finally {
      setCreating(false);
    }
  };

  const inputStyle = [st.input, { color: colors.ink, borderColor: colors.line, backgroundColor: colors.paper }];

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: pad, gap: 16 }}>
      <PageHeader
        title="Templates"
        subtitle="Tenant-authored messages, governed by two gates before anything sends."
        right={<Btn label="Refresh" icon="refresh" size="sm" onPress={load} />}
      />

      <View style={[st.card, { backgroundColor: colors.card, borderColor: colors.line }]}>
        <SectionLabel>New template</SectionLabel>
        <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <View style={{ flex: 1, minWidth: 160 }}>
            <Text style={[st.label, { color: colors.ink3 }]}>Key</Text>
            <TextInput
              value={templateKey}
              onChangeText={setTemplateKey}
              placeholder="e.g. salon-weekend-promo"
              placeholderTextColor={colors.ink4}
              autoCapitalize="none"
              style={inputStyle}
            />
          </View>
          <View style={{ flex: 1, minWidth: 160 }}>
            <Text style={[st.label, { color: colors.ink3 }]}>Display name</Text>
            <TextInput
              value={displayName}
              onChangeText={setDisplayName}
              placeholder="e.g. Weekend salon promo"
              placeholderTextColor={colors.ink4}
              style={inputStyle}
            />
          </View>
          <View style={{ minWidth: 180 }}>
            <Text style={[st.label, { color: colors.ink3 }]}>Message class</Text>
            <Select
              value={messageClassCode}
              onChange={setMessageClassCode}
              options={MESSAGE_CLASSES.map((c) => ({ value: c, label: CLASS_LABEL[c] }))}
            />
          </View>
          <Btn label={creating ? 'Creating…' : 'Create'} icon="plus" kind="primary" disabled={!canCreate} onPress={create} />
        </View>
        {messageClassCode === 'promotional' && (
          <Text style={{ color: colors.ink4, fontSize: 11, marginTop: 8 }}>
            Promotional messages are subject to consent, do-not-call, quiet hours and frequency caps.
          </Text>
        )}
      </View>

      {loading && <EmptyState title="Loading templates…" />}
      {err && !loading && <EmptyState icon="alert" title="Couldn't load templates" action={<Btn label="Retry" onPress={load} />} />}
      {!loading && !err && templates.length === 0 && (
        <EmptyState icon="inbox" title="No templates yet" body="Create your first tenant-authored template above." />
      )}
      {!loading && !err && templates.length > 0 && (
        <View style={[st.card, { backgroundColor: colors.card, borderColor: colors.line, padding: 0 }]}>
          <View style={[st.tr, st.trHead, { borderBottomColor: colors.line }]}>
            <Text style={[st.th, { color: colors.ink4, flex: 2 }]}>Template</Text>
            <Text style={[st.th, { color: colors.ink4, flex: 1 }]}>Class</Text>
            <Text style={[st.th, { color: colors.ink4, flex: 1 }]}>Type</Text>
            <Text style={[st.th, { color: colors.ink4, flex: 1 }]}>Created</Text>
          </View>
          {templates.map((t) => (
            <Pressable
              key={t.templateId}
              onPress={() => navigation.navigate('templateDetail', { templateId: t.templateId })}
              style={[st.tr, { borderBottomColor: colors.line }]}
            >
              <View style={{ flex: 2 }}>
                <Text style={{ color: colors.ink, fontSize: 13, fontWeight: '600' }}>{t.displayName}</Text>
                <Text style={{ color: colors.ink4, fontSize: 11 }}>{t.templateKey}</Text>
              </View>
              <Text style={{ color: colors.ink2, fontSize: 12, flex: 1, textTransform: 'capitalize' }}>{t.messageClassCode}</Text>
              <Text style={{ color: colors.ink3, fontSize: 12, flex: 1 }}>{t.isSystem ? 'System' : 'Tenant'}</Text>
              <Text style={{ color: colors.ink3, fontSize: 12, flex: 1 }}>{relTime(t.createdAt)}</Text>
              <Icon name="chevronRight" size={14} color={colors.ink3} />
            </Pressable>
          ))}
        </View>
      )}

      <Banner tone="info" icon="info">
        <Text style={{ color: colors.ink, fontSize: 12 }}>
          A template must pass Gate 1 (approval) and Gate 2 (permission) for a channel + region before it can
          send there. Open a template to manage both.
        </Text>
      </Banner>
    </ScrollView>
  );
}

const st = StyleSheet.create({
  card: { padding: 16, borderRadius: 2, borderWidth: 1 },
  label: { fontSize: 12, fontWeight: '600', marginBottom: 5 },
  input: { height: 36, borderWidth: 1, borderRadius: 2, paddingHorizontal: 10, fontSize: 13 },
  tr: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 12, borderBottomWidth: 1, gap: 8 },
  trHead: { paddingVertical: 8 },
  th: { fontSize: 11, fontWeight: '600', textTransform: 'uppercase' },
});
